/**
 * Pecas comuns das paginas do Cabal Next (home, /news, /news/:id): header com o menu, rodape, data-reveal.
 * Na home o menu usa ancoras (#secao) e acompanha a rolagem; nas outras paginas leva pra home (/#secao) e
 * "Noticias" fica ativo. CSS em next-home.css (raiz .nx).
 * Fase BETA (dono, 2026-10-10): o site so' recebe inscricoes -- menu Inicio/Noticias, botao "Inscrever Beta"
 * (modal BetaSignup), downloads desativados ("Em breve"), rede social so' o Instagram.
 * Conta (dono, 2026-10-17): deslogado = "Inscrever Beta" + "Entrar" (modal NextLogin); logado = menu ganha "Painel"
 * (/account) e no canto fica o nome do jogador com o menu Gerenciar conta / Sair.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Instagram, LogIn, LogOut, Menu, UserCog, UserPlus, X } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { BetaSignup } from './BetaSignup';
import { NextLogin } from './NextLogin';
import './next-home.css';

export const ASSET = '/assets/next';
export const HEADER_HEIGHT = 62;

// Redes do Cabal Next (dono: por enquanto so' o Instagram)
export const LINKS = {
  instagram: 'https://www.instagram.com/cabalnext/',
};

export const NAV = [
  { id: 'inicio', label: 'Início' },
  { id: 'noticias', label: 'Notícias' },
];
// so' logado; fica fora do NAV (a home acompanha a rolagem pelas secoes do NAV)
const PANEL_NAV = { id: 'painel', label: 'Painel' };

// O site rola o <body> (globals.css: html/body height 100% + overflow-x hidden), nao a janela
export const scroller = () => document.body;

/** data-reveal: o bloco entra (sobe + aparece) quando chega na tela, uma vez. deps = refaz quando o conteudo muda. */
export function useReveal(root: RefObject<HTMLElement | null>, deps: unknown[] = []) {
  useEffect(() => {
    const nodes = root.current?.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-visible)');
    if (!nodes?.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root, ...deps]);
}

/** Pagina nova abre no topo (o <body> guarda a rolagem da pagina anterior). */
export function useScrollTopOnMount() {
  useEffect(() => {
    scroller().scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);
}

type HeaderProps = {
  active: string;
  onHome?: boolean;                 // true = ancoras da propria pagina
  onNavigate?: (id: string) => void;
};

/** Nome do jogador logado + menu (Gerenciar conta -> /account, Sair -> home). */
function UserMenu() {
  const { user, account, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const name = account?.userName?.trim() || account?.id || user?.username || '';

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`nx-user${open ? ' is-open' : ''}`}>
      <button type="button" className="nx-user__btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="nx-user__avatar">{(name[0] || '?').toUpperCase()}</span>
        <span className="nx-user__name">{name}</span>
        <ChevronDown size={16} className="nx-user__chev" />
      </button>
      {open && (
        <div className="nx-user__menu" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/account');
            }}
          >
            <UserCog size={16} /> Gerenciar conta
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
              navigate('/');
            }}
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

export function NextHeader({ active, onHome = false, onNavigate }: HeaderProps) {
  const { isLoggedIn } = useAuth();
  const navRef = useRef<HTMLElement>(null);
  const [bar, setBar] = useState({ left: 0, width: 0 });
  const [betaOpen, setBetaOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // celular (<=560px): o menu do topo some e vira o botao de menu + painel embaixo do header (dono 2026-10-21)
  const [menuOpen, setMenuOpen] = useState(false);

  // barra do item ativo na borda do header: mede o link e desliza ate ele (nenhum ativo = some)
  const place = useCallback(() => {
    const link = navRef.current?.querySelector<HTMLElement>(`a[data-id="${active}"]`);
    setBar(link ? { left: link.offsetLeft, width: link.offsetWidth } : (b) => ({ ...b, width: 0 }));
  }, [active, isLoggedIn]);
  useLayoutEffect(place, [place]);
  // o menu fica centralizado entre o logo e o canto direito: quando o canto muda de largura (o nome do jogador chega
  // depois do login, botoes <-> nome) os links andam -- mede de novo sempre que o menu muda de tamanho, nao so' no
  // resize da janela (a barra parava no meio do caminho / a esquerda do "Painel" ao entrar no painel)
  useEffect(() => {
    const nav = navRef.current;
    window.addEventListener('resize', place);
    document.fonts?.ready.then(place);
    const observer = typeof ResizeObserver !== 'undefined' && nav ? new ResizeObserver(place) : null;
    if (observer && nav) observer.observe(nav);
    return () => {
      window.removeEventListener('resize', place);
      observer?.disconnect();
    };
  }, [place]);

  return (
    <header className="nx-header">
      <div className="nx-container nx-header__inner">
        {onHome ? (
          <a href="#inicio" className="nx-header__logo" aria-label="Cabal Next">
            <img src={`${ASSET}/logo-text.webp`} alt="Cabal Next" />
          </a>
        ) : (
          <Link to="/" className="nx-header__logo" aria-label="Cabal Next">
            <img src={`${ASSET}/logo-text.webp`} alt="Cabal Next" />
          </Link>
        )}
        <nav ref={navRef} className="nx-nav">
          {NAV.map((item) => {
            const className = item.id === active ? 'is-active' : undefined;
            if (onHome)
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  data-id={item.id}
                  className={className}
                  onClick={() => onNavigate?.(item.id)}
                >
                  {item.label}
                </a>
              );
            const to = item.id === 'noticias' ? '/news' : item.id === 'inicio' ? '/' : `/#${item.id}`;
            return (
              <Link key={item.id} to={to} data-id={item.id} className={className}>
                {item.label}
              </Link>
            );
          })}
          {isLoggedIn && (
            <Link
              to="/account"
              data-id={PANEL_NAV.id}
              className={active === PANEL_NAV.id ? 'is-active' : undefined}
            >
              {PANEL_NAV.label}
            </Link>
          )}
          <span
            className="nx-nav__bar"
            style={{ transform: `translateX(${bar.left}px)`, width: bar.width, opacity: bar.width ? 1 : 0 }}
          />
        </nav>
        {isLoggedIn ? (
          <UserMenu />
        ) : (
          <div className="nx-header__actions">
            <button type="button" className="nx-cta" onClick={() => setBetaOpen(true)}>
              <UserPlus size={18} />
              <span>Inscrever Beta</span>
            </button>
            <button type="button" className="nx-login" onClick={() => setLoginOpen(true)}>
              <LogIn size={17} />
              <span>Entrar</span>
            </button>
          </div>
        )}
        <button
          type="button"
          className="nx-burger"
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {menuOpen && (
        <nav className="nx-mnav">
          {NAV.map((item) => {
            const className = item.id === active ? 'is-active' : undefined;
            if (onHome)
              return (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={className}
                  onClick={() => {
                    setMenuOpen(false);
                    onNavigate?.(item.id);
                  }}
                >
                  {item.label}
                </a>
              );
            const to = item.id === 'noticias' ? '/news' : item.id === 'inicio' ? '/' : `/#${item.id}`;
            return (
              <Link key={item.id} to={to} className={className} onClick={() => setMenuOpen(false)}>
                {item.label}
              </Link>
            );
          })}
          {isLoggedIn && (
            <Link
              to="/account"
              className={active === PANEL_NAV.id ? 'is-active' : undefined}
              onClick={() => setMenuOpen(false)}
            >
              {PANEL_NAV.label}
            </Link>
          )}
        </nav>
      )}
      <BetaSignup open={betaOpen} onClose={() => setBetaOpen(false)} />
      <NextLogin open={loginOpen} onClose={() => setLoginOpen(false)} onSignup={() => setBetaOpen(true)} />
    </header>
  );
}

export function NextFooter() {
  return (
    <footer className="nx-footer">
      <div className="nx-container nx-footer__inner" data-reveal>
        <img className="nx-footer__logo" src={`${ASSET}/logo-text.webp`} alt="Cabal Next" loading="lazy" />
        <p className="nx-footer__motto">Beta chegando nos próximos dias. Garanta sua vaga!</p>
        <div className="nx-footer__right">
          <div className="nx-social">
            <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram do Cabal Next">
              <Instagram size={22} />
            </a>
          </div>
          <p className="nx-footer__copy">© {new Date().getFullYear()} Cabal Next. Todos os direitos reservados.</p>
        </div>
      </div>
    </footer>
  );
}
